import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["placement", "shape", "size", "tooltip", "tooltipProps", "TooltipContent", "disabled", "use", "block", "href"];
import { useTheme } from 'foundations-theming/utils';
import { Tooltip } from '../Tooltip/Tooltip';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { Button } from '../Button/Button';
import { IconButtonProvider } from './IconButtonProvider';
import { getIconButtonIconStyles } from './IconButton.helpers';
import { StyledButtonLink, UnstyledButtonLink } from '../Button/PrivateButtonLink';
import { StyledButton, StyledButtonTextContainer, UnstyledButton } from '../Button/PrivateButton';
import { useIconSize } from 'foundations-assets/icons/useIconSize';
import { useDropdownContext } from '../Dropdown/DropdownContext';
import { CSS_SPECIFICITY } from '../../constants';
import { ControlProvider } from '../Link/ControlContext';
import { isLinkUse } from '../Button/Button.helpers';
import { jsx as _jsx } from "react/jsx-runtime";
const getSize = size => {
  switch (size) {
    case 'xs':
      return 24;
    case 'sm':
      return 32;
    default:
      return 40;
  }
};
const getUnstyledSize = size => {
  switch (size) {
    case 'xs':
      return 12;
    case 'sm':
      return 16;
    default:
      return 18;
  }
};
const getUnstyledBlockSize = () => {
  return 26;
};
export const IconButton = _ref => {
  let {
      placement = 'top',
      shape = 'square',
      size = 'md',
      tooltip,
      tooltipProps,
      TooltipContent,
      disabled = false,
      use = 'tertiary-light',
      block = false,
      href
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const dropdownContext = useDropdownContext();
  const shorthandSize = toShorthandSize(size);

  // Generate icon style overrides for IconCircle descendants
  const iconStyleOverrides = getIconButtonIconStyles();
  const svgSize = useIconSize(shorthandSize === 'xs' ? '100' : shorthandSize === 'sm' ? '200' : '300');
  const buttonStyleOverrides = Object.assign({
    // ---------- These styles don't exist in `UIIconButton`, but are used here to center the icon ----------

    // Centers the icon when the icon button is a link (also is applied when the icon button is a button)
    display: 'inline-flex',
    // Ensures that we continue to respect the `inline` style
    alignItems: 'center',
    justifyContent: 'center',
    // Centers the icon when the icon button is a button
    [`&& ${StyledButtonTextContainer}`]: {
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
      blockSize: shorthandSize === 'xs' ? '14px' : shorthandSize === 'sm' ? '14px' : '18px'
    },
    // ----------
    // Default padding styles for IconButton - can be overridden by user token props
    paddingInlineStart: theme.space['0'],
    paddingInlineEnd: theme.space['0'],
    textAlign: 'center',
    textOverflow: 'clip',
    // Only apply these styles for navigation buttons (NextButton and PreviousButton)
    '&[data-button-type="navigation"]': Object.assign({}, disabled && {
      backgroundColor: `${theme.color['fill-tertiary-default']} !important`,
      borderRadius: `${theme.borderRadius['100']} !important`,
      borderColor: `${theme.color['border-core-disabled']} !important`
    }, {
      ':active:not([aria-disabled="true"])': {
        borderColor: theme.color['border-core-hover']
      }
    })
  }, (dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.isDropdown) && {
    [CSS_SPECIFICITY.FORCE_OVERRIDE]: {
      paddingInlineStart: theme.space['0'],
      paddingInlineEnd: theme.space['0']
    }
  }, {
    inlineSize: use.includes('link') ? 'auto' : getSize(shorthandSize)
  }, use === 'unstyled' ? {
    inlineSize: getUnstyledSize(shorthandSize),
    blockSize: getUnstyledBlockSize()
  } : undefined, href ? {
    blockSize: getSize(shorthandSize)
  } : undefined, {
    // ----------

    '& svg[data-icon-name]': {
      blockSize: svgSize,
      inlineSize: svgSize
    }
  }, shape === 'circle' ? {
    borderStartStartRadius: theme.borderRadius['full'],
    borderStartEndRadius: theme.borderRadius['full'],
    borderEndEndRadius: theme.borderRadius['full'],
    borderEndStartRadius: theme.borderRadius['full'],
    '& + &': {
      marginInlineStart: theme.space['0']
    }
  } : undefined, use === 'transparent' ? {
    [`${StyledButton} svg[data-icon-name], ${UnstyledButton} svg[data-icon-name], ${StyledButtonLink} svg[data-icon-name], ${UnstyledButtonLink} svg[data-icon-name]`]: {
      color: 'inherit'
    }
  } : undefined, {
    // TODO: Don't apply this style to `IconCircle`
    [`${StyledButtonLink} svg[data-icon-name], ${UnstyledButtonLink} svg[data-icon-name]`]: {
      color: 'inherit'
    }
  });
  return /*#__PURE__*/_jsx(ControlProvider, {
    isControl: isLinkUse(use),
    children: /*#__PURE__*/_jsx(IconButtonProvider, {
      buttonStyleOverrides: buttonStyleOverrides,
      iconStyleOverrides: iconStyleOverrides,
      children: /*#__PURE__*/_jsx(Tooltip, Object.assign({
        Content: TooltipContent,
        title: tooltip,
        placement: placement
      }, tooltipProps, {
        children: /*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
          disabled: disabled,
          use: use,
          block: block,
          responsive: false,
          size: shorthandSize,
          href: href
        }))
      }))
    })
  });
};